export type UserRole = 'admin' | 'coach' | 'faculty' | 'student';
export type UserVerificationStatus = 'pending' | 'verified';

export const FACULTY_ROLE_LABEL = 'P.E. Instructor';

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrator',
  coach: 'Coach',
  faculty: FACULTY_ROLE_LABEL,
  student: 'Student',
};

export function getRoleLabel(role: UserRole | string) {
  return role in USER_ROLE_LABELS
    ? USER_ROLE_LABELS[role as UserRole]
    : role.charAt(0).toUpperCase() + role.slice(1);
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  verificationStatus?: UserVerificationStatus;
  verifiedAt?: string;
  department?: string;
  studentId?: string;
  profileImageUrl?: string;
  idNumber?: string;
  assignedSport?: string;
}

export interface Equipment {
  id: string;
  name: string;
  category: string;
  quantity: number;
  available: number;
  condition: 'excellent' | 'good' | 'fair' | 'needs-repair';
  description: string;
  imageUrl?: string;
}

export interface BorrowRequest {
  id: string;
  equipmentId: string;
  equipmentName: string;
  userId: string;
  userName: string;
  userRole: UserRole;
  quantity: number;
  borrowDate: string;
  dueDate: string;
  returnDate?: string;
  status: 'pending' | 'approved' | 'borrowed' | 'returned' | 'overdue';
  purpose: string;
}

export interface Document {
  id: string;
  name: string;
  category: string;
  fileType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedAt: string;
  url: string;
}

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  createdAt: string;
  relatedId?: string; // ID of related request, equipment, etc.
}

export interface Feedback {
  id: string;
  requestId: string;
  userId: string;
  userName: string;
  equipmentName: string;
  rating: number; // 1-5 stars
  serviceRating: number; // 1-5 stars
  comments: string;
  suggestions?: string;
  createdAt: string;
}



// ==================== SPORTS REGISTRATION & EVALUATION ====================

export type SportGender = 'women' | 'men' | 'mixed';
export type CriteriaType = 'number' | 'boolean' | 'select';
export type RegistrationStatus = 'pending' | 'registered' | 'evaluated' | 'rejected';
export type PerformanceLevel = 'novice' | 'intermediate' | 'advanced';
export type MembershipType = 'interested' | 'applying';
export type CommitteeResult = 'W' | 'L';

export interface EvaluationCriteria {
  key: string;
  label: string;
  type: CriteriaType;
  min?: number;
  max?: number;
  options?: string[];
  weight?: number;
}

export interface PerformanceLevelConfig {
  label: string;
  level: PerformanceLevel;
  minScore: number;
  maxScore: number;
  points?: number;
}

export interface CommitteeConfig {
  eligibleLevels: PerformanceLevel[];
  resultStatuses: CommitteeResult[];
}

export interface SportCategory {
  id: string;
  name: string;
  slug: string;
  parentSlug?: string; // Groups sub-categories under a parent sport (e.g., 'codm' for 'codm-multiplayer' and 'codm-battle-royale')
  gender: SportGender;
  description: string;
  criteria: EvaluationCriteria[];
  performanceLevels: PerformanceLevelConfig[];
  committeeConfig?: CommitteeConfig;
  eliminationConfig?: EliminationConfig;
  requiredMatches?: number; // Number of match screenshots required before evaluation is enabled
  positions?: string[];
  positionCriteria?: Record<string, EvaluationCriteria[]>; // Position/category-specific evaluation criteria
  positionPerformanceLevels?: Record<string, PerformanceLevelConfig[]>; // Position/category-specific performance level thresholds
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SportRegistration {
  id: string;
  userId: string;
  sportId: string;
  sportName?: string;
  sportSlug?: string;
  parentSlug?: string;
  userName: string;
  userEmail: string;
  studentId?: string;
  department?: string;
  status: RegistrationStatus;
  membershipType?: MembershipType;
  positions?: string[];
  performanceLevel?: PerformanceLevel;
  performancePoints?: number;
  committeeEligible: boolean;
  committeeResult?: CommitteeResult;
  withExperience?: boolean;
  preRankingInfo?: string; // e.g., "5000+ points" for CODM, "Mythical Glory 100 Stars" for MLBB
  preRankingScreenshotUrl?: string; // URL of the account rank screenshot
  ign?: string; // In-Game Name for esports (CODM)
  matchScreenshots?: MatchScreenshot[]; // Coach-uploaded match screenshots with AI-scanned KDA
  aiSummary?: string;
  aiSummaryUpdatedAt?: string;
  aiSummarySource?: 'ai' | 'fallback';
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MatchScreenshot {
  id: string;
  imageUrl: string; // base64 data URL of the match screenshot
  scannedKDA?: {
    kills: number;
    deaths: number;
    assists: number;
    impact: number; // read directly from match screenshot
  };
  playerFound: boolean; // whether the AI found the player's IGN in the screenshot
  rawAIResponse?: string; // raw AI response for debugging
  uploadedAt: string;
}

export interface SportsRegistrationSettings {
  maxSportsPerStudent: number;
  updatedAt?: string;
}

export interface SportEvaluationScores {
  [criteriaKey: string]: number | boolean | string;
}

export interface SportEvaluation {
  id: string;
  registrationId: string;
  sportId: string;
  evaluatorId: string;
  evaluatorName: string;
  scores: SportEvaluationScores;
  totalScore: number;
  maxPossibleScore: number;
  percentage: number;
  computedLevel: PerformanceLevel;
  computedPoints: number;
  evaluatedPosition?: string;
  aiSummary?: string;
  aiSummaryUpdatedAt?: string;
  aiSummarySource?: 'ai' | 'fallback';
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== ELIMINATION ROUNDS ====================

export type EliminationRoundStatus = 'pending' | 'active' | 'completed';
export type EliminationMatchStatus = 'scheduled' | 'in_progress' | 'completed';
export type EliminationMatchResult = 'win' | 'loss' | 'draw' | 'bye';

export interface EliminationRound {
  id: string;
  sportId: string;
  sportName: string;
  roundNumber: number;
  name: string;
  status: EliminationRoundStatus;
  matches: EliminationMatch[];
  createdBy: string;
  createdByName: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EliminationMatch {
  id: string;
  roundId: string;
  participant1Id: string;
  participant1Name: string;
  participant2Id?: string;
  participant2Name?: string;
  score1?: number;
  score2?: number;
  winnerId?: string;
  winnerName?: string;
  result1?: EliminationMatchResult;
  result2?: EliminationMatchResult;
  status: EliminationMatchStatus;
  notes?: string;
}

export interface EliminationConfig {
  enabled: boolean;
  maxRounds?: number;
  requireEliminationBeforeEvaluation?: boolean;
}

// Pre-defined sport slugs for reference
export const SPORT_SLUGS = {
  WOMENS_BEACH_VOLLEYBALL: 'womens-beach-volleyball',
  BASEBALL: 'baseball',
  BADMINTON: 'badminton',
  KARATEDO_WOMEN: 'karatedo-women',
  CODM: 'codm',
  CODM_MULTIPLAYER: 'codm-multiplayer',
  CODM_BATTLE_ROYALE: 'codm-battle-royale',
  MLBB: 'mlbb',
} as const;